import { useEffect } from 'react';
import { api, type Dashboard } from './api';
import * as native from './native';

/**
 * Keeps the Home Screen widget in step with the app.
 *
 * Runs only inside the native shell — in a browser this is one `isNative()`
 * check and nothing else, no request and no work.
 *
 * It fetches the month alongside today rather than reusing whatever period the
 * Home screen happens to be showing: the widget always displays both figures,
 * and deriving one of them from a screen the user could have switched to
 * "last 90 days" would put a number on the Home Screen that means something
 * other than what it is labelled.
 */
export function useWidgetSync(revision: unknown): void {
  useEffect(() => {
    if (!native.isNative()) return;
    let cancelled = false;

    void (async () => {
      try {
        const [today, month] = await Promise.all([
          api.get<Dashboard>('/dashboard?period=today'),
          api.get<Dashboard>('/dashboard?period=month'),
        ]);
        if (cancelled) return;

        // The widget shows one bar for the month as a whole, so the budgets are
        // summed rather than picked from. A single category's budget would be
        // the wrong denominator for a total that covers all of them.
        const budgetMinor = month.budgets.reduce((sum, b) => sum + b.budgetMinor, 0);

        await native.writeWidgetSnapshot({
          spentTodayMinor: today.summary.spentMinor,
          spentMonthMinor: month.summary.spentMinor,
          budgetMonthMinor: budgetMinor > 0 ? budgetMinor : null,
          currency: month.currency,
          updatedAt: new Date().toISOString(),
        });
      } catch {
        // The widget keeps its previous figures. Failing to refresh it must
        // never surface as an error on a screen the user is reading.
      }
    })();

    return () => { cancelled = true; };
  }, [revision]);
}
